import { useState, useEffect, createContext, useContext, ReactNode, useCallback, useRef, Fragment } from "react";
import { QueryClientContext } from "@tanstack/react-query";
import { AuthUser } from "aws-amplify/auth";
import { Hub } from "aws-amplify/utils";
import {
  cognitoSignIn,
  cognitoSignUp,
  cognitoSignOut,
  cognitoConfirmSignUp,
  cognitoGetCurrentUser,
  cognitoResendCode,
  cognitoForgotPassword,
  cognitoConfirmForgotPassword,
  cognitoCompleteNewPassword,
  cognitoChangePassword,
  getCognitoTokens,
  seedCognitoSession,
  refreshCognitoSession,
  cognitoNow,
  CognitoTokens,
} from "@/lib/cognito";
import { REFRESH_LEEWAY_MS, msUntilRefresh } from "@/lib/tokenExpiry";
import { clearPerUserCaches, identityOf, isAuthStorageKey } from "@/lib/perUserCaches";

/** Bridge from the LEGACY admin, which stores its Cognito tokens in shared
 *  same-origin localStorage under flat `token`/`refreshToken` keys. Legacy and v2
 *  use the same Cognito user pool + app client, so those tokens are interchangeable —
 *  seeding an Amplify session from them means opening /v2 after logging into /admin
 *  doesn't require logging in again. No-ops (returns false) if the keys are absent or
 *  the refresh fails, so v2 just falls back to its own login. */
const seedFromLegacyAdminTokens = async (): Promise<boolean> => {
  if (typeof localStorage === "undefined") return false;
  const idToken = localStorage.getItem("token");
  const refreshToken = localStorage.getItem("refreshToken");
  if (!idToken || !refreshToken) return false;
  try {
    return await seedCognitoSession(idToken, refreshToken);
  } catch {
    return false;
  }
};

// Cleared on sign-out so the next user must pass MFA again.
export const MFA_VERIFIED_KEY = "photohawk.mfaVerified";

// Proactive refresh cadence (see the keep-alive effect in the provider).
/** Never re-check sooner than this, even if tokens look stale (skew safety net). */
export const MIN_REFRESH_DELAY_MS = 30_000;
/** After a transient refresh failure (offline, throttled), try again this soon. */
export const TRANSIENT_RETRY_MS = 60_000;
/** Cap a single timer so a long-lived page still re-checks now and then. */
const MAX_REFRESH_DELAY_MS = 30 * 60_000;

const NO_TOKENS: CognitoTokens = { idToken: null, accessToken: null, refreshToken: null };
const sameTokens = (a: CognitoTokens, b: CognitoTokens) =>
  a.idToken === b.idToken && a.accessToken === b.accessToken && a.refreshToken === b.refreshToken;

interface CognitoAuthContextType {
  user: AuthUser | null;
  tokens: CognitoTokens;
  /** Who requests are actually made as: the id token's `sub` (else the user's id); null when signed out.
   *  Key any per-user cache by this. */
  identity: string | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  signIn: (email: string, password: string) => Promise<{ success: boolean; error?: string; needsConfirmation?: boolean; needsNewPassword?: boolean }>;
  completeNewPassword: (newPassword: string) => Promise<{ success: boolean; error?: string }>;
  signUp: (email: string, password: string) => Promise<{ success: boolean; error?: string; needsConfirmation?: boolean }>;
  confirmSignUp: (email: string, code: string) => Promise<{ success: boolean; error?: string }>;
  resendConfirmationCode: (email: string) => Promise<{ success: boolean; error?: string }>;
  forgotPassword: (email: string) => Promise<{ success: boolean; error?: string }>;
  confirmForgotPassword: (email: string, code: string, newPassword: string) => Promise<{ success: boolean; error?: string }>;
  signOut: () => Promise<void>;
  refreshUser: () => Promise<void>;
  refreshTokens: () => Promise<CognitoTokens>;
  changePassword: (oldPassword: string, newPassword: string) => Promise<{ success: boolean; error?: string }>;
}

const CognitoAuthContext = createContext<CognitoAuthContextType | undefined>(undefined);

export const CognitoAuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [tokens, setTokens] = useState<CognitoTokens>({
    idToken: null,
    accessToken: null,
    refreshToken: null,
  });
  const [isLoading, setIsLoading] = useState(true);

  const refreshTokens = useCallback(async (): Promise<CognitoTokens> => {
    const newTokens = await getCognitoTokens();
    setTokens((prev) => (sameTokens(prev, newTokens) ? prev : newTokens));
    return newTokens;
  }, []);

  // Keep the session alive while any page under this provider is open (the apex
  // workspace chooser is often left open for hours; Cognito id/access tokens live ~1h):
  //  - a timer refreshes ~5 min before the earlier of the id/access expiries;
  //  - returning to the tab (visibilitychange/focus — timers are throttled or frozen in
  //    background tabs and while the machine sleeps) or coming back online re-checks and
  //    refreshes if within ~5 min;
  //  - whenever Amplify refreshes on its own (an API call found the token expired), the
  //    context tokens are re-synced so nothing reads a stale copy.
  // Only a refresh the server REJECTS (refresh token expired/revoked) signs the user out
  // here — that is the one case that should show the login screen. Network/throttling
  // failures keep the session and retry.
  useEffect(() => {
    if (!user) return;
    let disposed = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let inFlight: Promise<void> | null = null;

    const schedule = (delayMs: number) => {
      clearTimeout(timer);
      const ms = Math.min(Math.max(delayMs, MIN_REFRESH_DELAY_MS), MAX_REFRESH_DELAY_MS);
      timer = setTimeout(() => void ensureFresh(), ms);
    };

    const adopt = (next: CognitoTokens) => {
      setTokens((prev) => (sameTokens(prev, next) ? prev : next));
      schedule(msUntilRefresh(next, cognitoNow(), REFRESH_LEEWAY_MS) ?? MAX_REFRESH_DELAY_MS);
    };

    const ensureFresh = (): Promise<void> => {
      if (inFlight) return inFlight;
      inFlight = (async () => {
        const result = await refreshCognitoSession(REFRESH_LEEWAY_MS);
        if (disposed) return;
        if (result.status === "ok") {
          adopt(result.tokens);
        } else if (result.status === "signedOut") {
          clearTimeout(timer);
          setUser(null);
          setTokens(NO_TOKENS);
          try { sessionStorage.removeItem(MFA_VERIFIED_KEY); } catch { /* ignore */ }
        } else {
          schedule(TRANSIENT_RETRY_MS);
        }
      })().finally(() => {
        inFlight = null;
      });
      return inFlight;
    };

    const onWake = () => {
      if (typeof document === "undefined" || document.visibilityState === "visible") void ensureFresh();
    };

    const stopHub = Hub.listen("auth", ({ payload }) => {
      if (payload.event !== "tokenRefresh" || disposed) return;
      void getCognitoTokens().then((next) => {
        if (!disposed && next.idToken) adopt(next);
      });
    });

    document.addEventListener("visibilitychange", onWake);
    window.addEventListener("focus", onWake);
    window.addEventListener("online", onWake);
    void ensureFresh();

    return () => {
      disposed = true;
      clearTimeout(timer);
      stopHub();
      document.removeEventListener("visibilitychange", onWake);
      window.removeEventListener("focus", onWake);
      window.removeEventListener("online", onWake);
    };
  }, [user]);

  const refreshUser = async () => {
    try {
      let currentUser = await cognitoGetCurrentUser();
      // No Amplify session yet — try adopting a legacy-admin session from shared
      // localStorage (same pool + client + origin), so /admin → /v2 needs no re-login.
      if (!currentUser && (await seedFromLegacyAdminTokens())) {
        currentUser = await cognitoGetCurrentUser();
      }
      setUser(currentUser);
      if (currentUser) {
        await refreshTokens();
      } else {
        setTokens({ idToken: null, accessToken: null, refreshToken: null });
      }
    } catch {
      setUser(null);
      setTokens({ idToken: null, accessToken: null, refreshToken: null });
    }
  };

  // Amplify keeps ONE session per origin (localStorage), so a sign-in or sign-out in ANOTHER tab silently
  // swaps the tokens this tab sends while its React state still shows the old user. Re-read the user
  // whenever that can have happened: another tab touched the Cognito keys (storage event), Amplify says
  // the user signed in/out, or the token in hand belongs to someone other than the user on screen.
  const refreshUserRef = useRef<() => Promise<void>>(async () => {});
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (isAuthStorageKey(e.key)) void refreshUserRef.current();
    };
    window.addEventListener("storage", onStorage);
    const stopHub = Hub.listen("auth", ({ payload }) => {
      if (payload.event === "signedIn" || payload.event === "signedOut") void refreshUserRef.current();
    });
    return () => {
      window.removeEventListener("storage", onStorage);
      stopHub();
    };
  }, []);
  const tokenIdentity = identityOf(tokens.idToken);
  useEffect(() => {
    if (user && tokenIdentity && user.userId && tokenIdentity !== user.userId) void refreshUserRef.current();
  }, [user, tokenIdentity]);

  useEffect(() => {
    const initAuth = async () => {
      setIsLoading(true);
      try {
        await refreshUser();
      } finally {
        setIsLoading(false);
      }
    };
    initAuth();
  }, []);

  refreshUserRef.current = refreshUser;

  const handleSignIn = async (email: string, password: string) => {
    try {
      const result = await cognitoSignIn(email, password);
      
      if (result.nextStep?.signInStep === "CONFIRM_SIGN_UP") {
        return { success: false, needsConfirmation: true };
      }
      
      if (result.nextStep?.signInStep === "CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED") {
        return { success: false, needsNewPassword: true };
      }
      
      await refreshUser();
      return { success: true };
    } catch (error: any) {
      console.error("Sign in error:", error);
      
      if (error.name === "UserNotConfirmedException") {
        return { success: false, needsConfirmation: true, error: "Please confirm your email first." };
      }
      
      return { 
        success: false, 
        error: error.message || "Failed to sign in. Please check your credentials." 
      };
    }
  };

  const handleCompleteNewPassword = async (newPassword: string) => {
    try {
      await cognitoCompleteNewPassword(newPassword);
      await refreshUser();
      return { success: true };
    } catch (error: any) {
      console.error("Complete new password error:", error);
      return {
        success: false,
        error: error.message || "Failed to set new password.",
      };
    }
  };

  const handleSignUp = async (email: string, password: string) => {
    try {
      const result = await cognitoSignUp(email, password);
      
      if (result.nextStep?.signUpStep === "CONFIRM_SIGN_UP") {
        return { success: true, needsConfirmation: true };
      }
      
      return { success: true };
    } catch (error: any) {
      console.error("Sign up error:", error);
      
      if (error.name === "UsernameExistsException") {
        return { success: false, error: "An account with this email already exists." };
      }
      
      return { 
        success: false, 
        error: error.message || "Failed to sign up. Please try again." 
      };
    }
  };

  const handleConfirmSignUp = async (email: string, code: string) => {
    try {
      await cognitoConfirmSignUp(email, code);
      return { success: true };
    } catch (error: any) {
      console.error("Confirm sign up error:", error);
      return { 
        success: false, 
        error: error.message || "Invalid confirmation code." 
      };
    }
  };

  const handleResendCode = async (email: string) => {
    try {
      await cognitoResendCode(email);
      return { success: true };
    } catch (error: any) {
      console.error("Resend code error:", error);
      return { 
        success: false, 
        error: error.message || "Failed to resend confirmation code." 
      };
    }
  };

  const handleSignOut = async () => {
    try {
      await cognitoSignOut();
      setUser(null);
      setTokens({ idToken: null, accessToken: null, refreshToken: null });
      try { sessionStorage.removeItem(MFA_VERIFIED_KEY); } catch { /* ignore */ }
    } catch (error) {
      console.error("Sign out error:", error);
    }
  };

  const handleChangePassword = async (oldPassword: string, newPassword: string) => {
    try {
      await cognitoChangePassword(oldPassword, newPassword);
      return { success: true };
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : "Could not change password" };
    }
  };

  const handleForgotPassword = async (email: string) => {
    try {
      await cognitoForgotPassword(email);
      return { success: true };
    } catch (error: any) {
      console.error("Forgot password error:", error);
      return { 
        success: false, 
        error: error.message || "Failed to send reset code." 
      };
    }
  };

  const handleConfirmForgotPassword = async (email: string, code: string, newPassword: string) => {
    try {
      await cognitoConfirmForgotPassword(email, code, newPassword);
      return { success: true };
    } catch (error: any) {
      console.error("Confirm forgot password error:", error);
      return { 
        success: false, 
        error: error.message || "Failed to reset password." 
      };
    }
  };

  const identity = tokenIdentity || (user && user.userId) || null;

  return (
    <CognitoAuthContext.Provider
      value={{
        user,
        tokens,
        identity,
        isLoading,
        isAuthenticated: !!user,
        signIn: handleSignIn,
        completeNewPassword: handleCompleteNewPassword,
        signUp: handleSignUp,
        confirmSignUp: handleConfirmSignUp,
        resendConfirmationCode: handleResendCode,
        forgotPassword: handleForgotPassword,
        confirmForgotPassword: handleConfirmForgotPassword,
        signOut: handleSignOut,
        refreshUser,
        refreshTokens,
        changePassword: handleChangePassword,
      }}
    >
      <IdentityBoundary identity={identity}>{children}</IdentityBoundary>
    </CognitoAuthContext.Provider>
  );
};

/**
 * When the identity moves AWAY from a signed-in user (sign-out, another user signing in here or in
 * another tab), drop every per-user cache (lib/perUserCaches: all React Query data, the workspace
 * snapshot, the uploader's remembered destinations) and remount the tree below, so no component state —
 * a fetched workspace list, the upload queue, a selection — survives into the next user's session.
 * Done during render (derived-state pattern), BEFORE any child renders under the new identity, so the
 * previous user's data is never painted for even one frame. Signing in from signed-out changes nothing.
 */
const IdentityBoundary = ({ identity, children }: { identity: string | null; children: ReactNode }) => {
  const queryClient = useContext(QueryClientContext);
  const [state, setState] = useState({ identity, epoch: 0 });
  if (identity !== state.identity) {
    const leavingUser = state.identity !== null;
    if (leavingUser) clearPerUserCaches(queryClient);
    setState({ identity, epoch: leavingUser ? state.epoch + 1 : state.epoch });
  }
  return <Fragment key={state.epoch}>{children}</Fragment>;
};

export const useCognitoAuth = () => {
  const context = useContext(CognitoAuthContext);
  if (context === undefined) {
    throw new Error("useCognitoAuth must be used within a CognitoAuthProvider");
  }
  return context;
};
